import { useCallback, useEffect, useRef } from 'react'

import { useCreditInfo } from '@/lib/hooks'
import { SOCIAL_CREDITS_REFRESH_DEBOUNCE_MS } from './socialCredits'

interface UseRefreshCreditsOnEdgeOptions {
  /**
   * The universe busy flag: a busy to idle edge refreshes the balance
   * (settlement just returned or charged credits). Omit when the caller only
   * refreshes on its own events.
   */
  isBusy?: boolean
}

/**
 * Keeps the profile's credit balance fresh around social holds (E1): the
 * profile is loaded once at login, so `available = balance - held` would go
 * stale. Returns `refreshCredits()` for request successes (update, resume),
 * reaching the wizard review and opening Activity; also refreshes on the busy
 * to idle edge. At most one refresh per SOCIAL_CREDITS_REFRESH_DEBOUNCE_MS:
 * calls inside the window collapse into one trailing refresh at its end, so
 * the last event is never lost.
 */
export function useRefreshCreditsOnEdge(options: UseRefreshCreditsOnEdgeOptions = {}) {
  const { isBusy } = options

  const { mutate: refreshProfile } = useCreditInfo()
  const lastRefreshRef = useRef<number | null>(null)
  const trailingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshProfileRef = useRef(refreshProfile)
  const wasBusyRef = useRef(isBusy)

  useEffect(() => {
    refreshProfileRef.current = refreshProfile
  }, [refreshProfile])

  useEffect(() => {
    return () => {
      if (trailingRef.current !== null) {
        clearTimeout(trailingRef.current)
      }
    }
  }, [])

  const refreshCredits = useCallback(() => {
    const run = () => {
      trailingRef.current = null
      lastRefreshRef.current = Date.now()
      // Best effort: an auth context without a profile refresher (tests, embeds) skips it.
      const refreshProfile = refreshProfileRef.current
      if (typeof refreshProfile !== 'function') {
        return
      }
      void Promise.resolve(refreshProfile()).catch(() => undefined)
    }
    const last = lastRefreshRef.current
    const elapsed = last === null ? Number.POSITIVE_INFINITY : Date.now() - last
    if (elapsed >= SOCIAL_CREDITS_REFRESH_DEBOUNCE_MS) {
      run()
      return
    }
    if (trailingRef.current === null) {
      trailingRef.current = setTimeout(run, SOCIAL_CREDITS_REFRESH_DEBOUNCE_MS - elapsed)
    }
  }, [])

  useEffect(() => {
    if (wasBusyRef.current === true && isBusy === false) {
      refreshCredits()
    }
    wasBusyRef.current = isBusy
  }, [isBusy, refreshCredits])

  return refreshCredits
}
